import React, { useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSpring, animated } from "react-spring";
import { ChevronLeft, ChevronRight } from "react-feather";
import styled, { ThemeContext } from "styled-components/macro";

import { BackDropContainer } from "./BackDropContainer";
import { AppLoader } from "../Loader/AppLoader";
import { HeaderContainer } from "./HeaderContainer";
import { SideMenuContainer } from "./SideMenuContainer";
import { Side } from "./side";
import { OPEN_SIDE_MENU } from "../../store/actions/sideMenuActions";

import Constants from "../../constants";
import { getSideMenuContentLoading, getSideMenuOpen } from "../../store/sagas/selectors";

import { useBreakpoint } from "../../utilities/media-js-breakpoints";
import { useMenuActivityCallback } from "../../utilities/utilityHooks";

interface CloseSideMenuButtonProps {
    side: Side;
}

const SideMenuButton = styled.div<CloseSideMenuButtonProps>`
    cursor: pointer;
    pointer-events: all;
    height: 64px;
    width: 64px;
    margin-top: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 3;
    background-color: ${props => props.theme.sideMenu.backgroundColor};
    backdrop-filter: ${props => (props.theme.sideMenu.blur ? "blur(5px)" : "none")};
    border-radius: ${props => (props.side === Side.Right ? "7px 0 0 7px" : "0 7px 7px 0px")};
    transition: background 0.2s ease-in-out;
    &:hover {
        background: rgba(255, 255, 255, 0.8);
    }
    &:active {
        background: rgba(255, 255, 255, 1);
    }
`;

const SecondaryBackDropContainer = styled.div`
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    max-height: 100%;
    overflow: hidden;
    display: flex;
    align-items: stretch;
    overflow: hidden;
    flex-direction: column;
    background: ${props => props.theme.sideMenu.backgroundColor};
`;

export const SideMenu = ({
    isDisabled,
    side,
    header,
    children,
}: {
    isDisabled: boolean;
    side: Side;
    header?: any;
    children: any;
}) => {
    const themeContext = useContext(ThemeContext);
    const loading: boolean = useSelector(getSideMenuContentLoading);
    const isOpen: boolean = useSelector(getSideMenuOpen);
    const dispatch = useDispatch();

    const breakpoints = useBreakpoint();
    const isHighRes = breakpoints.xxxl ? Constants.highResSideMenuWidth : Constants.sideMenuWidth;

    const containerProps = {
        ...useSpring({
            width: isOpen ? `${isHighRes}px` : "0px",
        }),
        boxShadow: themeContext.sideMenu.shadow,
        position: "relative" as any,
        pointerEvents: "all" as any,
    };

    const renderButtonIcon = () => {
        if (isOpen) {
            return side === Side.Left ? (
                <ChevronLeft size={22} color="#000" />
            ) : (
                <ChevronRight size={22} color="#000" />
            );
        }

        return side === Side.Left ? <ChevronRight size={22} color="#000" /> : <ChevronLeft size={22} color="#000" />;
    };

    const throttleInterval = 200;
    const registerMenuActivity = useMenuActivityCallback(throttleInterval);
    return (
        <SideMenuContainer onMouseMove={registerMenuActivity} side={side}>
            {side === Side.Right && (
                <SideMenuButton
                    side={Side.Right}
                    className={isDisabled ? "disabled" : ""}
                    aria-hidden="true"
                    onClick={() => {
                        dispatch({ type: OPEN_SIDE_MENU, sideMenuOpen: !isOpen });
                    }}
                >
                    {renderButtonIcon()}
                </SideMenuButton>
            )}
            <animated.div style={containerProps}>
                <BackDropContainer>
                    <SecondaryBackDropContainer>
                        {loading ? (
                            <AppLoader color={themeContext.appLoader.color} height={200} width={80} />
                        ) : (
                            <>
                                {header ? <HeaderContainer>{header}</HeaderContainer> : null}
                                {children}
                            </>
                        )}
                    </SecondaryBackDropContainer>
                </BackDropContainer>
            </animated.div>

            {side === Side.Left && (
                <SideMenuButton
                    side={Side.Left}
                    className={isDisabled ? "disabled" : ""}
                    aria-hidden="true"
                    onClick={() => {
                        dispatch({ type: OPEN_SIDE_MENU, sideMenuOpen: !isOpen });
                    }}
                >
                    {renderButtonIcon()}
                </SideMenuButton>
            )}
        </SideMenuContainer>
    );
};
